import type { MotionValue } from 'motion/react';
import { useScroll } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

const checkHasScroll = (element: HTMLDivElement, maxHeight?: number): boolean =>
  element.scrollHeight > (maxHeight ?? Math.round(element.getBoundingClientRect().height));

interface UseScrollProgressProps {
  readonly maxHeight?: number;
  readonly container: React.MutableRefObject<HTMLDivElement | null>;
  readonly onScrollEnd?: (scrollEnd: boolean) => void;
}

interface UseScrollProgressReturnProps {
  readonly hasScroll: boolean;
  readonly scrollYProgress: MotionValue<number>;
}

export const useScrollProgress = ({
  container,
  maxHeight,
  onScrollEnd,
}: UseScrollProgressProps): UseScrollProgressReturnProps => {
  const [hasScroll, setHasScroll] = useState<boolean>(true);
  const scrollEndRef = useRef<boolean>(false);

  const { scrollYProgress } = useScroll({
    container,
  });

  useEffect(() => {
    const current = container.current;
    /* istanbul ignore if -- @preserve - can't mock null ref */
    if (current == null) return;

    setHasScroll(checkHasScroll(current, maxHeight));

    const observer = new MutationObserver(() => {
      setHasScroll(checkHasScroll(current, maxHeight));
    });

    observer.observe(current, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [container, maxHeight]);

  useEffect(() => {
    /* istanbul ignore if -- @preserve - can't mock null ref */
    if (scrollEndRef.current) return;

    const handleChange = (latest: number): void => {
      // we using 0.99 instead of 1 because of the floating point precision
      if (latest >= 0.99) {
        scrollEndRef.current = true;
        onScrollEnd?.(true);
      }
    };

    const unsubscribe = scrollYProgress.on('change', handleChange);

    return () => {
      unsubscribe();
    };
  }, [scrollYProgress, onScrollEnd]);

  return { hasScroll, scrollYProgress };
};
